import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { OrgAnalyticsContext, OrgAnalyticsDimensionType } from 'graphql/types';

import {
  encodeDataId,
  useGetSupervisorAnalyticsData
} from 'modules/Insights/Notes/hooks/useGetSupervisorAnalyticsData';
import { useGetSearchLabels } from 'modules/Insights/hooks/useGetSearchLabels';
import { useSupervisorInsightContext } from 'modules/Insights/Notes/SupervisorInsightContextProvider';

import { PieChartData, transformLabelDataSupervisor } from './utils';
import { PageSize } from './hooks/usePieChartDataPagination';
import ClientComponent from './ClientComponent';

const SupervisorClientChart: React.FC = () => {
  const labelValues = useGetSearchLabels();

  const [totalData, setTotaldata] = useState<PieChartData>([]);
  const [getCurrentPage, setGetCurrentPage] = useState(1);

  const { data, loading, isEmpty, refetch } = useGetSupervisorAnalyticsData(
    OrgAnalyticsDimensionType.MetricsByNoteClient,
    labelValues,
    OrgAnalyticsContext.Supervisor
  );

  const { currentPeriod } = useSupervisorInsightContext();

  const [isFetchMoreData, setIsFetchMoreData] = useState(false);

  const { startDate, endDate } = currentPeriod ?? {};

  const dataset = useMemo(() => {
    if (isEmpty) {
      return [];
    } else {
      return transformLabelDataSupervisor(
        data?.orgAnalytics?.analyticsResult.items,
        'CLIENT'
      );
    }
  }, [data?.orgAnalytics?.analyticsResult.items, isEmpty]);

  const paginationPageno = useRef(1);

  useEffect(() => {
    if (isFetchMoreData) {
      setTotaldata((previous) => {
        return [...previous, ...dataset];
      });
      setIsFetchMoreData(false);
    } else {
      setTotaldata(dataset);

      setGetCurrentPage(1);
      paginationPageno.current = 1;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataset]);

  useEffect(() => {
    const dataLength = totalData.length;
    const totalPages = Math.ceil(dataLength / PageSize);
    if (
      getCurrentPage === totalPages - 1 &&
      data?.orgAnalytics?.analyticsResult.hasMoreData
    ) {
      paginationPageno.current = paginationPageno.current + 1;
      const encodedId = encodeDataId({
        dimensionValue: OrgAnalyticsDimensionType.MetricsByNoteClient,
        contextValue: OrgAnalyticsContext.Supervisor,
        labels: labelValues,
        dateRangeValue: {
          startDate: startDate || '',
          endDate: endDate || ''
        },
        pageNumber: paginationPageno.current
      });
      const refetchQuery = async () => {
        await refetch({
          variables: {
            id: encodedId
          }
        });
      };
      refetchQuery();
      setIsFetchMoreData(true);
    }
  }, [
    currentPeriod,
    data?.orgAnalytics?.analyticsResult.hasMoreData,
    endDate,
    getCurrentPage,
    labelValues,
    refetch,
    startDate,
    totalData,
    totalData.length
  ]);

  return (
    <ClientComponent
      data={totalData}
      loading={loading}
      isEmpty={isEmpty}
      setGetCurrentPage={setGetCurrentPage}
    />
  );
};
export default memo(SupervisorClientChart);
